import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Inbox, DollarSign, Clock } from "lucide-react";
import { api, ADDONS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["new", "contacted", "scheduled", "completed"];
const statusColor = {
  new: "bg-[#005CEE] text-white",
  contacted: "bg-[#FF4500] text-white",
  scheduled: "bg-amber-500 text-black",
  completed: "bg-emerald-500 text-black",
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pipeline_value: 0, new_count: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([api.get("/bookings"), api.get("/bookings/stats")]);
      setBookings(b.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const cards = [
    { icon: Inbox, label: "Total requests", value: stats.total },
    { icon: DollarSign, label: "Pipeline value", value: `$${stats.pipeline_value.toLocaleString()}` },
    { icon: Clock, label: "New / unactioned", value: stats.new_count },
  ];

  return (
    <div className="dash min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-[#1E293B] sticky top-0 bg-[#0B0F19]/90 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-head font-extrabold tracking-tighter text-lg">OUTDOEM<span className="text-[#005CEE]">.</span></span>
            <span className="font-mono2 text-xs text-[#94A3B8] hidden sm:inline">/ BOOKINGS</span>
          </div>
          <button onClick={doLogout} className="flex items-center gap-2 text-xs font-mono2 text-[#94A3B8] hover:text-white transition-colors" data-testid="logout-btn">
            <LogOut size={14} /> LOGOUT
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-head font-extrabold tracking-tighter text-4xl mb-8">Booking requests</h1>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {cards.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="bg-[#111827] border border-[#1E293B] rounded-2xl p-6">
              <c.icon size={18} className="text-[#005CEE]" />
              <p className="font-mono2 text-3xl mt-4">{c.value}</p>
              <p className="text-xs text-[#94A3B8] mt-1">{c.label}</p>
            </motion.div>
          ))}
        </div>

        {loading ? (
          <p className="font-mono2 text-sm text-[#94A3B8]">Loading…</p>
        ) : bookings.length === 0 ? (
          <div className="bg-[#111827] border border-[#1E293B] rounded-2xl p-16 text-center" data-testid="empty-state">
            <p className="font-mono2 text-sm text-[#94A3B8]">No booking requests yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bookings-list">
            {bookings.map((b) => (
              <motion.div key={b.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="bg-[#111827] border border-[#1E293B] rounded-2xl p-6 grid lg:grid-cols-12 gap-4 items-start" data-testid="booking-row">
                <div className="lg:col-span-4">
                  <p className="font-semibold text-lg">{b.name}</p>
                  <p className="text-sm text-[#94A3B8]">{b.email} · {b.phone}</p>
                  <p className="text-sm text-[#94A3B8] mt-1">{b.address}</p>
                </div>
                <div className="lg:col-span-3 text-sm text-[#94A3B8] font-mono2">
                  <p>{Number(b.square_footage).toLocaleString()} sq ft</p>
                  <p>{ADDONS.filter((a) => b.addons?.[a.key]).map((a) => a.label).join(", ") || "no add-ons"}</p>
                  {b.preferred_date && <p>Date: {b.preferred_date}</p>}
                  {b.notes && <p className="mt-1 italic text-[#64748B] not-italic">"{b.notes}"</p>}
                </div>
                <div className="lg:col-span-2">
                  <p className="font-mono2 text-2xl text-[#005CEE]">${Number(b.estimate).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                  <p className="text-[10px] text-[#64748B] font-mono2">ESTIMATE</p>
                </div>
                <div className="lg:col-span-3 flex flex-wrap gap-2 justify-start lg:justify-end">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => changeStatus(b.id, s)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-mono2 uppercase tracking-wide transition-all ${b.status === s ? statusColor[s] : "bg-[#0B0F19] text-[#64748B] hover:text-white border border-[#1E293B]"}`}
                      data-testid={`status-${s}-${b.id}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
